import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Icon } from "../components/Icon";
import { LinkWrapper } from "../components/LinkWrapper";
import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from "../components/Tooltip";
import { cn } from "../lib/utils";
import * as React from "react";

/**
 * A pill-styled tab navigation root (controlled via `value`/`defaultValue`),
 * composed with NavTabPillList, NavTabPillTrigger, and NavTabPillContent.
 * Use it for primary navigation between top-level sections in compact,
 * sidebar-style layouts; for standard underlined tabs within a content area,
 * use Tabs instead.
 * @summary Pill-styled tab navigation root.
 */
const NavTabPill = TabsPrimitive.Root;
const NavTabPillList = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(TabsPrimitive.List, {
  ref: ref,
  className: cn("flex items-center gap-1.5", className),
  ...props
}));
NavTabPillList.displayName = TabsPrimitive.List.displayName;
const NavTabPillTrigger = React.forwardRef(({
  className,
  icon,
  children,
  href,
  target,
  rel,
  replace,
  shallow,
  ...props
}, ref) => {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const setRefs = React.useCallback(node => {
    triggerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  const handleTooltipOpenChange = open => {
    var _triggerRef$current;
    // Label is already visible on the expanded (active) pill — skip the tooltip.
    if (open && ((_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.dataset.state) === "active") {
      setTooltipOpen(false);
      return;
    }
    setTooltipOpen(open);
  };
  const triggerClassName = cn("pill-button touch-hitbox group whitespace-nowrap group-data-[state=active]:pl-2.5 text-sm font-medium", "data-[state=active]:pr-0", "data-[state=active]:bg-selected data-[state=active]:text-foreground", "disabled:pointer-events-none disabled:opacity-100", "data-[state=active]:overflow-hidden data-[state=active]:shrink", "transition-[padding,background-color,color] duration-200 motion-reduce:transition-none", className);
  const content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    visual: icon,
    size: "sm"
  }), /*#__PURE__*/React.createElement("div", {
    className: cn("relative grid grid-cols-[0fr] transition-[grid-template-columns] duration-200 group-data-[state=active]:grid-cols-[1fr] overflow-hidden motion-reduce:transition-none")
  }, /*#__PURE__*/React.createElement("span", {
    className: "overflow-hidden min-w-0",
    style: {
      maskImage: "linear-gradient(to right, black calc(100% - 8px), transparent)"
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "group-data-[state=active]:pl-1.5 group-data-[state=active]:opacity-100 group-data-[state=inactive]:opacity-0 group-data-[state=active]:pr-2.5 whitespace-nowrap"
  }, children))));
  const trigger = href ? /*#__PURE__*/React.createElement(TabsPrimitive.Trigger, {
    ref: setRefs,
    asChild: true,
    ...props
  }, /*#__PURE__*/React.createElement(LinkWrapper, {
    href: href,
    target: target,
    rel: rel,
    replace: replace,
    className: triggerClassName
  }, content)) : /*#__PURE__*/React.createElement(TabsPrimitive.Trigger, {
    ref: setRefs,
    className: triggerClassName,
    ...props
  }, content);
  if (!children) {
    return trigger;
  }

  // TooltipTrigger sets `data-state` (open/closed). Wrap in a span so that
  // attribute doesn't overwrite the Tabs trigger's `data-state` (active/
  // inactive), which drives selected styles and the label expand animation.
  return /*#__PURE__*/React.createElement(TooltipProvider, {
    delayDuration: 300,
    skipDelayDuration: 0
  }, /*#__PURE__*/React.createElement(TooltipRoot, {
    open: tooltipOpen,
    onOpenChange: handleTooltipOpenChange,
    disableHoverableContent: true
  }, /*#__PURE__*/React.createElement(TooltipTrigger, {
    asChild: true
  }, /*#__PURE__*/React.createElement("span", {
    className: "inline-flex"
  }, trigger)), /*#__PURE__*/React.createElement(TooltipContent, null, children)));
});
NavTabPillTrigger.displayName = TabsPrimitive.Trigger.displayName;
const NavTabPillContent = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(TabsPrimitive.Content, {
  ref: ref,
  className: cn("contents focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className),
  ...props
}));
NavTabPillContent.displayName = TabsPrimitive.Content.displayName;
export { NavTabPill, NavTabPillContent, NavTabPillList, NavTabPillTrigger };
//# sourceMappingURL=NavTabPill.js.map